import React, { useState } from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";
import videoParser from "js-video-url-parser";
import { useTheme } from "styled-components";
import { useDispatch } from "react-redux";

import { getStartSeconds, getEndSeconds } from "../../../../../../common/parameters";
import { IEmbedData, IProvider, ITaskEditorProps } from "../../../../../../common/types";

import { setItem, hasItem } from "../../../../../cookie";
import { IRadioEvent } from "../../../../../types";
import Icon from "../../../../../icon";
import { post } from "../../../../../actions/general";
import { editTask } from "../../../../../store/plan/actions";

import { InputText, Label, InputNumber, Radio } from "../../../../Form";

import EditorBar, { FlexLeft, FlexRight, Center } from "../../../EditorBar";
import Wrapper from "../../../Wrapper";
import ViewContainer from "../../../ViewContainer";

import useExerciseEditor from "../../Common/useExerciseEditor";
import ChangeColor from "../../Common/ChangeColor";

import {
    StyledInputs,
    LinkRow,
    ArrowWrapper,
    YoutubeInputWrapper,
    IframeWrapper,
    TimeRow,
    Time,
    Hour,
    Minute,
    Second,
    Dots,
    Container,
    LeftSide,
    RightSide,
} from "./styles";
import messages from "./messages";

// prezi regex: /(?:\/p\/)(?:edit\/)?([\w-]+)/.exec(url)[1]
const EmbedEditor = ({ taskIndex, plan, inlineEditorOpen, toggleInlineEditor, publicView, viewProps, playable }: ITaskEditorProps) => {
    const dispatch = useDispatch();
    const theme = useTheme();

    const [url, setUrl] = useState("");
    const changeHref = (e: React.FormEvent<HTMLInputElement>) => {
        const data = plan.tasks[taskIndex].data as IEmbedData;
        const newUrl = e.currentTarget.value;
        setUrl(newUrl);
        if (data.provider === IProvider.Prezi) {
            const result = /(?:\/p\/)(?:edit\/)?([\w-]+)/.exec(newUrl);
            if (result && result[1]) {
                const id = result[1];
                dispatch(editTask({ taskIndex, changingProp: "href", val: id }));
                saveHref();
            }
        } else {
            const result = videoParser.parse(e.currentTarget.value)!;
            if (result && result.id) {
                dispatch(editTask({ taskIndex, changingProp: "href", val: result.id }));
                saveHref();
            }
        }
    };
    const saveHref = () => {
        dispatch(post("embed-save-href", {
            planId: plan._id,
            index: taskIndex,
            href: (plan.tasks[taskIndex].data as IEmbedData).href
        }));
    };

    const changeStartHour = (e: React.FormEvent<HTMLInputElement>) => {
        const startHour = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(9, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "startHour", val: startHour }));
        saveStartTime();
    };
    const changeStartMinute = (e: React.FormEvent<HTMLInputElement>) => {
        const startMinute = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(59, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "startMinute", val: startMinute }));
        saveStartTime();
    };
    const changeStartSecond = (e: React.FormEvent<HTMLInputElement>) => {
        const startSecond = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(59, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "startSecond", val: startSecond }));
        saveStartTime();
    };
    const saveStartTime = () => {
        const data = plan.tasks[taskIndex].data as IEmbedData;
        const { startSecond, startMinute, startHour } = data;
        dispatch(post("embed-save-start-time", { planId: plan._id, index: taskIndex, startSecond, startMinute, startHour }));
    };
    const changeEndHour = (e: React.FormEvent<HTMLInputElement>) => {
        const endHour = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(9, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "endHour", val: endHour }));
        saveEndTime();
    };
    const changeEndMinute = (e: React.FormEvent<HTMLInputElement>) => {
        const endMinute = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(59, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "endMinute", val: endMinute }));
        saveEndTime();
    };
    const changeEndSecond = (e: React.FormEvent<HTMLInputElement>) => {
        const endSecond = e.currentTarget.value === "" ? "" : Math.max(0, Math.min(59, +e.currentTarget.value));
        dispatch(editTask({ taskIndex, changingProp: "endSecond", val: endSecond }));
        saveEndTime();
    };
    const saveEndTime = () => {
        const data = plan.tasks[taskIndex].data as IEmbedData;
        const { endHour, endMinute, endSecond } = data;
        dispatch(post("embed-save-end-time", { planId: plan._id, index: taskIndex, endSecond, endMinute, endHour }));
    };
    const changeProvider = (e: IRadioEvent) => {
        const provider = e.target.value as IProvider;
        setUrl("");
        dispatch(editTask({ taskIndex, changingProp: "provider", val: provider }));
        dispatch(editTask({ taskIndex, changingProp: "href", val: "" }));
        dispatch(editTask({ taskIndex, changingProp: "endHour", val: "" }));
        dispatch(editTask({ taskIndex, changingProp: "endMinute", val: "" }));
        dispatch(editTask({ taskIndex, changingProp: "endSecond", val: "" }));
        dispatch(editTask({ taskIndex, changingProp: "startHour", val: "" }));
        dispatch(editTask({ taskIndex, changingProp: "startMinute", val: "" }));
        dispatch(editTask({ taskIndex, changingProp: "startSecond", val: "" }));
        dispatch(post("embed-save-provider", { planId: plan._id, index: taskIndex, provider }));
    }
    const {
        openChanger,
        handleChangeOpen,
        setPopupRef,
    } = useExerciseEditor({ plan, inlineEditorOpen, taskIndex });
    if (!plan)
        return (
            <div>
                <FormattedMessage {...messages.loading} />
            </div>
        );
    const data = plan.tasks[taskIndex].data as IEmbedData;
    const { startHour, startMinute, startSecond, endHour, endMinute, endSecond, provider = IProvider.YouTube } = data;

    const forwardProps = {
        plan,
        taskIndex,
        openChanger,
        handleChangeOpen,
        setPopupRef,
    }

    return (
        <div>
            {inlineEditorOpen && (
                <ViewContainer viewProps={viewProps}>
                    <Wrapper>
                        <Container>
                            <LeftSide>
                                <Radio
                                    name="provider"
                                    label={<FormattedMessage {...messages.provider} />}
                                    options={[
                                        {
                                            value: IProvider.YouTube,
                                            label: "YouTube",
                                        },
                                        {
                                            value: IProvider.Vimeo,
                                            label: "Vimeo",
                                        },
                                        {
                                            value: IProvider.Prezi,
                                            label: "Prezi",
                                        },
                                    ]}
                                    value={provider}
                                    onChange={changeProvider}
                                />
                            </LeftSide>
                            <RightSide>
                                <StyledInputs>
                                    <LinkRow>
                                        <YoutubeInputWrapper>
                                            <InputText label={"Link"} value={url} onChange={changeHref} />
                                        </YoutubeInputWrapper>
                                        <ArrowWrapper>
                                            <Icon i="chevron-right" />
                                        </ArrowWrapper>
                                        <InputText
                                            onChange={() => { }}
                                            label={"ID"}
                                            value={data.href || ""}
                                            disabled
                                        />
                                    </LinkRow>
                                    <TimeRow>
                                        {provider !== IProvider.Prezi && (
                                            <Time>
                                                <Label>
                                                    <FormattedMessage {...messages.start} />
                                                </Label>
                                                <Hour>
                                                    <InputNumber name="startHour" value={startHour?.toString() || ""} onChange={changeStartHour} />
                                                </Hour>
                                                <Dots>:</Dots>
                                                <Minute>
                                                    <InputNumber name="startMinute" value={startMinute?.toString() || ""} onChange={changeStartMinute} />
                                                </Minute>
                                                <Dots>:</Dots>
                                                <Second>
                                                    <InputNumber name="startSecond" value={startSecond?.toString() || ""} onChange={changeStartSecond} />
                                                </Second>
                                            </Time>
                                        )}
                                        {provider === IProvider.YouTube && (
                                            <Time>
                                                <Label>
                                                    <FormattedMessage {...messages.end} />
                                                </Label>
                                                <Hour>
                                                    <InputNumber name="endHour" value={endHour?.toString() || ""} onChange={changeEndHour} />
                                                </Hour>
                                                <Dots>:</Dots>
                                                <Minute>
                                                    <InputNumber name="endMinute" value={endMinute?.toString() || ""} onChange={changeEndMinute} />
                                                </Minute>
                                                <Dots>:</Dots>
                                                <Second>
                                                    <InputNumber name="endSecond" value={endSecond?.toString() || ""} onChange={changeEndSecond} />
                                                </Second>
                                            </Time>
                                        )}
                                    </TimeRow>
                                </StyledInputs>
                                {data.href && provider === IProvider.YouTube && (
                                    <IframeWrapper>
                                        <iframe
                                            title={"youtube-editor-iframe"}
                                            width="640"
                                            height="360"
                                            src={
                                                "https://www.youtube-nocookie.com/embed/" +
                                                data.href +
                                                "?start=" +
                                                getStartSeconds(data) +
                                                "&end=" +
                                                getEndSeconds(data)
                                            }
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    </IframeWrapper>
                                )}

                                {data.href && provider === IProvider.Vimeo && (
                                    <IframeWrapper>
                                        <iframe
                                            src={`https://player.vimeo.com/video/${data.href}?color=${theme!.c.boost.slice(1)}#t=${data.startHour || 0}h${data.startMinute || 0}m${data.startSecond || 0}s`}
                                            width="640"
                                            height="360"
                                            frameBorder="0"
                                            allow="autoplay; fullscreen; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    </IframeWrapper>
                                )}
                                {data.href && provider === IProvider.Prezi && (
                                    <IframeWrapper>
                                        <iframe
                                            src={`https://prezi.com/p/${data.href}/embed`}
                                            width="640"
                                            height="360"
                                            frameBorder="0"
                                            allow="autoplay; fullscreen; picture-in-picture"
                                            allowFullScreen
                                        ></iframe>
                                    </IframeWrapper>
                                )}

                            </RightSide>
                        </Container>
                    </Wrapper>
                </ViewContainer>
            )}
            {!publicView && (
                <EditorBar>
                    <FlexLeft></FlexLeft>
                    <Center
                        disabled={!playable}
                        onClick={() => {
                            saveHref();
                            toggleInlineEditor(!inlineEditorOpen);
                            setItem("embedDone", "1", 60 * 60 * 24 * 365, "/");
                        }}
                        className={cn(!(inlineEditorOpen && !playable) && !hasItem("embedDone") && "pulsate")}
                    >
                        {inlineEditorOpen ? (
                            <FormattedMessage {...messages.stopEdit} />
                        ) : (
                            <FormattedMessage {...messages.startEdit} />
                        )}
                    </Center>
                    <FlexRight>
                        <ChangeColor
                            {...forwardProps}
                            color={data?.color}
                        />
                    </FlexRight>
                </EditorBar>
            )}
        </div>
    );
}

export default EmbedEditor;
